import React, { useContext, useEffect, useRef, useState } from "react";

import FrontPageLogo from "../../components/Logo/Logo";
import { GiEntryDoor } from "react-icons/gi";
import { Redirect, useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import QuestionHeader from "../../components/QuestionHeader/QuestionHeader";
import Conditional from "../../components/Conditional/Conditional";
import Feedback from "../../shared/api/FeedbackApi";
import { default as MuiButton } from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import CasinoIcon from "@mui/icons-material/Casino";
import CloseIcon from "@mui/icons-material/Close";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import {
  Alert,
  Button,
  Backdrop,
  Badge,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,

  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { FaCrown } from "react-icons/fa";
import { MdHeight } from "react-icons/md";
import { width } from "@mui/system";
import { SelectChangeEvent } from "@mui/material/Select";
import SettingSelector from "../../components/SettingSelector/SettingSelector";
import SettingSwitch from "../../components/SettingSwitch/SettingSwitch";
import CardDeck from "../../components/CardDeck/CardDeck";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { GameContext } from "../../shared/GameContext";
import CardDeckBrowser from "../../components/CardDeckBrowser/CardDeckBrowser";
import CardDeckCreator from "../../components/CardDeckCreator/CardDeckCreator";
import useRank5Auth from "../../shared/useRank5Auth";
import { publicWebUrl, serverUrl } from "../../shared/runtime";
import Deck from "../../shared/api/DeckApi";
import {
  CardDeckPreview,
  Cue,
  CueTypes,
  GameConfig,
  Language,
  Player,
  PlayerEndResults,
  Results,
  ResultCard,
  TurnInfo,
} from "../../shared/interfaces/GameLogicTypes";
import { Languages } from "../../shared/language/languages";
import useSocketContext from "../../shared/useSocketContext";
import useGameContext from "../../shared/useGameContext";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import {
  convertBackendToFrontendDecks,
  getLanguageByLanguageId,
  isDeckSelected,
} from "../../shared/helper/functions";
import CardDeckViewer from "../../components/CardDeckViewer/CardDeckViewer";
import LanguageSelector from "../../components/LanguageSelector/LanguageSelector";
import _ from "underscore";
import PlayerCard from "../../components/PlayerCard/PlayerCard";
import PlayerAvatar, { getPlayerColor } from "../../components/PlayerAvatar/PlayerAvatar";
import { Colors } from "../OldLobby/Colors";
import Game from "../../components/Game/Game";
import EndPanel from "../../components/EndPanel/EndPanel";
import { useTheme } from "@emotion/react";
import { Theme as MuiTheme } from "@mui/material/styles";
import { CreateDeckConfigMenu } from "../../components/CreateDeckConfigMenu/CreateDeckConfigMenu";
import TopicSelector from "../../components/TopicSelector/TopicSelector";
import { track } from "../../shared/analytics";
import AdminMessageDialog from "../../components/AdminMessageDialog/AdminMessageDialog";
import LobbyPresetSelector, { PresetId, EntryMode, GameModeEntrySelector, EntryModeBadge, getPresetConfig, getAutoPreset, detectPreset, detectEntryMode } from "../../components/LobbyPresetSelector/LobbyPresetSelector";
import { QRCodeCanvas } from "qrcode.react";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import FriendPoolEditor from "../../components/FriendPoolEditor/FriendPoolEditor";

// Modes grouped by family. Versus modes have no czar (everyone answers at once, scored against
// the group); co-op modes do (one player answers, the rest predict). The two families can be
// mixed in the Custom tab, but they make a match feel — and last — very differently.
const MODE_GROUPS: { family: EntryMode; headerKey: string; modes: { value: string; labelKey: string }[] }[] = [
  {
    family: "VERSUS",
    headerKey: "lobby_modes_versus",
    modes: [
      { value: "STANDARD_VERSUS", labelKey: "ftl_standard" },
      { value: "RUN_WITH_THE_HERD", labelKey: "herd_game_mode_label" },
    ],
  },
  {
    family: "COOP",
    headerKey: "lobby_modes_coop",
    modes: [
      { value: "STANDARD", labelKey: "ftl_standard" },
      { value: "FOLLOW_THE_LEADER", labelKey: "ftl_follow_the_leader" },
      // REVERSE_ROUND is retired from the lobby — the backend still runs it, but hosts kept
      // picking it and it doesn't hold up. Re-add the entry here to bring it back.
      { value: "PICASSO", labelKey: "picasso_game_mode_label" },
    ],
  },
];

const VERSUS_MODES = ["STANDARD_VERSUS", "RUN_WITH_THE_HERD"];
// Picasso draws from its own prompt pool, so for the purpose of "are these two families mixed?"
// it belongs to neither. Mirrors isGroupOnlyMatch() in backend/src/game.ts.
const NEUTRAL_MODES = ["PICASSO"];
// …but its round is still about the people at the table — one player impersonates another and
// everyone has to spot the fake. In a Versus lobby ("friends, co-workers & classmates") that
// needs saying, exactly like the co-op modes do.
const FAMILIARITY_MODES = ["PICASSO"];
/** The two base ranking modes are mutually exclusive — a game has one target, not two. */
const BASE_MODES = ["STANDARD", "STANDARD_VERSUS"];

/**
 * Smallest lobby these modes can run in. Mirrors minPlayersForModes() in backend/src/game.ts —
 * versus scores everyone against the group's aggregate, which with two players is decided
 * entirely by the submission-order tie-break, and elimination modes need someone left standing.
 */
export function minPlayersForModes(enabledModes: string[]): number {
  if (enabledModes.length === 0) return 2;
  if (enabledModes.includes("STANDARD_VERSUS")) return 3;
  const eliminationOnly = enabledModes.every(
    (m) => m === "FOLLOW_THE_LEADER" || m === "RUN_WITH_THE_HERD"
  );
  return eliminationOnly ? 3 : 2;
}

/** Minutes we assume a single question costs, start to results. */
const MINUTES_PER_QUESTION = 3;
/** Above this, the host has to acknowledge how long the match will run. */
const LONG_MATCH_MINUTES = 45;

/**
 * How many questions a match actually plays.
 * Co-op rounds are a full czar rotation (one question per player); versus rounds are one
 * question flat. Mixing the families keeps the czar rotation, so it costs the co-op amount.
 */
function estimateQuestions(enabledModes: string[], roundLimit: number, playerCount: number) {
  const czarBased = enabledModes.includes("STANDARD") || enabledModes.includes("FOLLOW_THE_LEADER");
  return czarBased ? roundLimit * Math.max(1, playerCount) : roundLimit;
}

export default function Lobby() {
  const { isAuthenticated, loginWithPopup, getAccessTokenSilently, getAccessTokenWithPopup, user } =
    useRank5Auth();

  const [canStart, setCanStart] = useState(false);

  const [lobbyPhase, setLobbyPhase] = useState("L");
  const numberOfRoundsChoices = ["1", "2", "3", "5", "10", "15"];
  const roundTimerChoices = ["75", "120", "180", "300", "600"];
  const maxSkipsChoices = ["0", "1", "4", "8", "12", "50"];
  const defaultAllAboutMe = false;
  const defaultWaitForHost = false;
  const defaultEnableJoinDuringRound = true;
  const [selectedDecks, setSelectedDecks] = useState<Array<CardDeckPreview>>([]);
  const [enabledModes, setEnabledModes] = useState<string[]>(["STANDARD", "FOLLOW_THE_LEADER"]);
  const [numberOfRounds, setNumberOfRounds] = React.useState(numberOfRoundsChoices[2]);
  const [roundTimer, setRoundTimer] = React.useState(roundTimerChoices[3]);
  const [maxSkips, setMaxSkips] = React.useState(maxSkipsChoices[3]);
  const [selectDecks, setSelectDecks] = useState(false);
  const [deckBrowserBrowseOnly, setDeckBrowserBrowseOnly] = useState(false);
  const [createDecks, setCreateDecks] = useState(false);
  const submittedPlayerName = useGameContext().nameState;
  const setSubmittedPlayerName = useGameContext().setNameState;
  const [playerName, setPlayerName] = useState("");
  const setName = useGameContext().setNameState;
  const [lastEditedDeck, setLastEditedDeck] = useState("");
  const lobbyKey = useGameContext().lobbyKey;
  const setLobbyKey = useGameContext().setLobbyKey;
  const socket = useSocketContext().socket;
  const [playerCount, setPlayerCount] = useState(1);
  const [players, setPlayers] = useState<{ [key: string]: Player }>({});
  const [playerId, setPlayerId] = useState(sessionStorage.getItem("playerId") || "");
  const [hostId, setHostId] = useState("");
  const [isHost, setIsHost] = useState(false);
  const [isCodeVisible, setIsCodeVisible] = useState(false);
  const [previewDeckId, setPreviewDeckId] = useState("");
  const [deckPreferences, setDeckPreferences] = useState<{ [deckId: string]: number }>({});
  const [playerEndResults, setPlayerEndResults] = useState<PlayerEndResults>();
  const [wasDCed, setWasDCed] = useState(false);
  const [reconnectStatus, setReconnectStatus] = useState("");
  const playerIdRef = useRef(sessionStorage.getItem("playerId") || "");
  const joinParamsRef = useRef<{
    name: string;
    lobbyKey: string;
    playerId: string;
    auth0Sub?: string;
    language?: string;
  }>({
    name: "",
    lobbyKey: "",
    playerId: "",
    auth0Sub: undefined,
    language: undefined,
  });
  const lastRejoinRef = useRef(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [kickTarget, setKickTarget] = useState<string | null>(null);
  const [readyPlayerIds, setReadyPlayerIds] = useState<string[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [adminMessage, setAdminMessage] = useState<string | null>(null);
  const [resultCards, setResultCards] = useState<ResultCard[]>([]);
  const [gameTurnHistory, setGameTurnHistory] = useState<TurnInfo[]>([]);
  const hasJoinedRef = useRef(false);
  const hasPreSelectedDecks = useRef(!!sessionStorage.getItem("preSelectedDeckIds"));
  const autoSelectingDecksRef = useRef(false);
  const hasSeenRunningRef = useRef(false);
  const playerCountRef = useRef(1);
  const [deselectionTarget, setDeselectionTarget] = useState<CardDeckPreview | null>(null);
  const [skipDeselectionConfirm, setSkipDeselectionConfirm] = useState(
    () => localStorage.getItem("skipDeselectionConfirm") === "true"
  );
  const [createConfigMenuAnchor, setCreateConfigMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<PresetId>("PARTY");
  // Versus (compete vs the group) or Co-op (predict your friends). Chosen before the presets.
  const [entryMode, setEntryMode] = useState<EntryMode>("COOP");
  const [entryChosen, setEntryChosen] = useState(false); // gates the preset grid until the vibe is picked
  // The host has settled the preset themselves — either by clicking a tile or by going into the
  // config panel. From then on, players joining must never move the lobby off it.
  const [manualPreset, setManualPreset] = useState(false);
  // The host has hand-edited a config value. The preset's own config must then stop being
  // re-applied, or the next join would overwrite the edit.
  const [configTouched, setConfigTouched] = useState(false);
  const [rightTab, setRightTab] = useState<"presets" | "decks" | "custom">("presets");
  /** A host edit in the config panel pins both the preset and the values against later joins. */
  const markConfigTouched = () => {
    setManualPreset(true);
    setConfigTouched(true);
  };
  const [deckMode, setDeckMode] = useState<"topics" | "manual">("topics");
  const [selectedTopics, setSelectedTopics] = useState<string[]>([
    "self_reflection", "values_identity", "life_stories",
    "relationships", "social_dynamics", "pop_culture",
    "politics", "career", "travel",
  ]);
  const [nsfwEnabled, setNsfwEnabled] = useState(false);
  const [includeLowQuality, setIncludeLowQuality] = useState(false);
  const [topicLanguage, setTopicLanguage] = useState<string[]>(() => {
    const browserLang = (navigator.language || "en").split("-")[0].toLowerCase();
    const isoToAppLang: Record<string, string> = {
      en: "en", de: "de", fr: "fr", es: "es", ja: "jp", ru: "ru",
      it: "it", zh: "cn", ko: "kr", fil: "ph", tl: "ph", hi: "in",
      ar: "sa", pt: "pt", id: "id", sv: "se", pl: "pl", da: "dn", nl: "nl",
      tr: "tr",
    };
    return [isoToAppLang[browserLang] || "en"];
  });

  const [qrOpen, setQrOpen] = useState(false);

  // Friend pool state
  const [friendPoolEnabled, setFriendPoolEnabled] = useState(true);
  const [friendPoolNames, setFriendPoolNames] = useState<{ id: number; name: string }[]>([]);
  const friendPoolLoadedRef = useRef(false);
  const history = useHistory();
  const baseUrl = useLocation().pathname.replace(/(lobby\/[A-Z]*)/, "");
  let inviteLink = publicWebUrl(`/join/${lobbyKey}`);
  const { t } = useTranslation();

  let theme = useTheme();
  let isMobile = useMediaQuery(theme.breakpoints.down("md"));


  // connect socket

  useEffect(() => {
    const count = Object.keys(players).length;
    setPlayerCount(count);
  }, [players])


  useEffect(() => {
    const minPlayers = minPlayersForModes(enabledModes);
    const picassoOnly = enabledModes.length > 0 && enabledModes.every((m) => m === "PICASSO");
    const hasPicassoDecks = selectedDecks.some((d: any) => d.hasPicassoCards);
    const picassoBlocked = picassoOnly && !hasPicassoDecks;
    const hasContent = deckMode === "topics"
      ? selectedTopics.length > 0
      : selectedDecks.length !== 0;
    // Nobody can ready up before the host has picked Co-op or Versus — the whole config
    // (and how long the match runs) hangs off that choice.
    if (entryChosen && playerCount >= minPlayers && hasContent && !picassoBlocked) {
      setCanStart(true);
    } else {
      setCanStart(false);
    }
  }, [playerCount, selectedDecks, enabledModes, deckMode, selectedTopics, entryChosen]);

  // Auto-select preset by player count (unless user manually picked one)
  useEffect(() => {
    if (!isHost || lobbyPhase !== "L" || manualPreset) return;
    const auto = getAutoPreset(playerCount, entryMode);
    if (auto !== selectedPreset) {
      setSelectedPreset(auto);
    }
  }, [playerCount, isHost, lobbyPhase, manualPreset, entryMode]);

  // Apply preset config whenever preset, entry mode, or player count changes
  useEffect(() => {
    if (!isHost || lobbyPhase !== "L" || selectedPreset === "CUSTOM" || configTouched) return;
    const config = getPresetConfig(selectedPreset, playerCount, entryMode);
    setNumberOfRounds(String(config.roundLimit));
    setRoundTimer(String(config.roundTimer));
    setMaxSkips(String(config.maxSkips));
    setEnabledModes(config.enabledModes);
    setWaitForHost(config.waitForHost);
    setEnableJoinDuringRound(config.enableJoinDuringRound);
  }, [selectedPreset, playerCount, entryMode, configTouched]);

  const handlePresetSelect = (preset: PresetId) => {
    // Deliberate even when it happens to match the current auto-pick: the host chose it, so a
    // later join must not move the lobby to a different preset. Picking a preset is also a fresh
    // start for its config, so any earlier hand-edits stop being protected.
    setManualPreset(true);
    setConfigTouched(false);
    setSelectedPreset(preset);
    setTimeout(() => openTab("decks"), 400);
  };

  // The two families have different presets, so picking a family always lands on that
  // family's default rather than carrying an impossible selection across.
  const handleEntrySelect = (mode: EntryMode) => {
    setEntryMode(mode);
    setManualPreset(false);
    setConfigTouched(false);
    setSelectedPreset(getAutoPreset(playerCount, mode));
    setEntryChosen(true);
  };

  const handleOpenCustom = () => {
    setSelectedPreset("CUSTOM");
    setManualPreset(true);
    openTab("custom");
  };

  /**
   * Tab switch. A host who goes straight to Custom without touching the preset tiles has still
   * made a deliberate choice, so opening that tab counts as picking a family — otherwise the
   * lobby would sit there with Ready greyed out and no obvious way to un-grey it.
   */
  const openTab = (tab: "presets" | "decks" | "custom") => {
    if (tab === "custom" && isHost) {
      setEntryChosen(true);
      // Opening the config panel is as deliberate as clicking a tile — hold the preset.
      setManualPreset(true);
    }
    setRightTab(tab);
  };

  /**
   * Toggle a mode in the Custom tab. Families mix freely, but the two base ranking modes
   * (STANDARD / STANDARD_VERSUS) are mutually exclusive — a turn is scored against either the
   * czar or the group, never both — so enabling one clears the other.
   */
  const handleModeToggle = (mode: string) => {
    let next: string[];
    if (enabledModes.includes(mode)) {
      next = enabledModes.filter((m) => m !== mode);
    } else {
      next = BASE_MODES.includes(mode)
        ? [...enabledModes.filter((m) => !BASE_MODES.includes(m)), mode]
        : [...enabledModes, mode];
    }
    if (next.length === 0) return;
    setEnabledModes(next);
    setSelectedPreset("CUSTOM");
    setManualPreset(true);
    setConfigTouched(true);
    setEntryMode(detectEntryMode(next));
  };

  const modesAreMixed =
    enabledModes.some((m) => VERSUS_MODES.includes(m)) &&
    enabledModes.some((m) => !VERSUS_MODES.includes(m) && !NEUTRAL_MODES.includes(m));
  // A Versus lobby is pitched at people who may not know each other well, so any mode that leans
  // on knowing them gets the same heads-up the mixed-family case gets.
  const needsFamiliarity =
    modesAreMixed || (entryMode === "VERSUS" && enabledModes.some((m) => FAMILIARITY_MODES.includes(m)));

  const estimatedQuestions = estimateQuestions(enabledModes, parseInt(numberOfRounds), playerCount);
  const estimatedMinutes = estimatedQuestions * MINUTES_PER_QUESTION;
  const matchIsLong = estimatedMinutes > LONG_MATCH_MINUTES;

  // Long-match acknowledgement. Remembering the acknowledged length means the host isn't
  // nagged every time they toggle ready, but a *longer* config asks again.
  const [longMatchOpen, setLongMatchOpen] = useState(false);
  const ackedMinutesRef = useRef(0);
  const pendingStartRef = useRef<(() => void) | null>(null);

  /** Run `proceed`, unless the host is about to commit everyone to a very long match. */
  const guardLongMatch = (proceed: () => void) => {
    if (isHost && matchIsLong && estimatedMinutes > ackedMinutesRef.current) {
      pendingStartRef.current = proceed;
      setLongMatchOpen(true);
      return;
    }
    proceed();
  };

  const requiredPlayers = minPlayersForModes(enabledModes);
  const startBlockedReason = !canStart
    ? !entryChosen
      ? t("lobby_waiting_for_mode")
      : playerCount < requiredPlayers
        ? t("lobby_needs_players", { n: requiredPlayers })
        : deckMode === "topics" && selectedTopics.length > 0
          ? "No decks match your topics"
          : "Select a deck first"
    : "";

  useEffect(() => {
    //eject players without lobby key (name comes from server on reconnect)
    if (lobbyKey === "") {
      history.push("/play");
    }
  }, [lobbyKey]);

  // Get or create a tab-specific playerId (sessionStorage is cleared when the tab is closed)
  const getOrCreatePlayerId = () => {
    let id = sessionStorage.getItem("playerId");
    if (!id) {
      id = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      });
      sessionStorage.setItem("playerId", id);
    }
    return id;
  };

  // Keep join params current — the socket "connect"/"rejoinRequired" handlers live in the
  // mount-only effect below and would otherwise capture a stale (often empty) name.
  // Declared BEFORE the mount effect so the ref is populated by the time it runs.
  useEffect(() => {
    playerIdRef.current = playerId || getOrCreatePlayerId();
    joinParamsRef.current = {
      name: playerName || submittedPlayerName, // server-authoritative name wins
      lobbyKey,
      playerId: getOrCreatePlayerId(),
      auth0Sub: user?.sub || undefined,
      // The lobby takes its language from whoever opened it; admin intermissions target it.
      language: (i18n.language || "en").slice(0, 2).toLowerCase(),
    };
  }, [playerName, submittedPlayerName, lobbyKey, user?.sub, playerId]);

  // Re-join the lobby on the current socket. Used by the reconnect handlers and
  // the disconnect overlay button; throttled so rejoinRequired can't loop.
  const rejoin = (throttled = true) => {
    if (!socket.connected) socket.connect();
    if (throttled && Date.now() - lastRejoinRef.current < 2000) return;
    lastRejoinRef.current = Date.now();
    if (joinParamsRef.current.lobbyKey) {
      socket.emit("joinLobby", joinParamsRef.current);
    }
  };

  useEffect(() => {
    let isMounted = true;
    getOrCreatePlayerId();
    // Store the lobbyKey so GameContextProvider can restore it on page refresh
    sessionStorage.setItem("lobbyKey", lobbyKey);

    socket.on("gamePhase", (gamePhase: string) => {
      if (gamePhase === "RUNNING") {
        hasSeenRunningRef.current = true;
        track("game_started", { lobbyKey });
      }
      if (gamePhase === "FINISHED") {
        track("game_finished", { lobbyKey });
        setIsReady(false);
        setReadyPlayerIds([]);
      }
      if (gamePhase === "FINISHED" && !hasSeenRunningRef.current) {
        sessionStorage.removeItem("playerId");
        sessionStorage.removeItem("lobbyKey");
        history.push("/play");
        return;
      }
      setLobbyPhase(gamePhase);
    });
    socket.on("gameConfig", (gameConfig: GameConfig) => {
      setNumberOfRounds(gameConfig.roundLimit.toString());
      setRoundTimer(gameConfig.roundTimer.toString());
      setMaxSkips(gameConfig.maxSkips.toString());
      setAllAboutMe(gameConfig.allAboutMe);
      setEnableJoinDuringRound(gameConfig.enableJoinDuringRound);
      setWaitForHost(gameConfig.waitForHost);
      if (gameConfig.enabledModes) setEnabledModes(gameConfig.enabledModes);
      if (gameConfig.deckMode && !hasPreSelectedDecks.current) setDeckMode(gameConfig.deckMode);
      if (gameConfig.selectedTopics) setSelectedTopics(gameConfig.selectedTopics);
      if (gameConfig.nsfwEnabled !== undefined) setNsfwEnabled(gameConfig.nsfwEnabled);
      if (gameConfig.includeLowQuality !== undefined) setIncludeLowQuality(gameConfig.includeLowQuality);
      if (gameConfig.topicLanguage !== undefined) setTopicLanguage(gameConfig.topicLanguage);
      // Detect entry mode + which preset matches the received config
      if (gameConfig.enabledModes) {
        const detectedEntry = detectEntryMode(gameConfig.enabledModes);
        setEntryMode(detectedEntry);
        // Only skip the vibe step when a real choice has been made. The backend's default config
        // has no `preset`; a host only broadcasts a preset after picking Versus/Co-op — so this
        // stays false for a fresh lobby (host sees the vibe tiles first) and true on rejoin.
        if (gameConfig.preset) setEntryChosen(true);
        const detected = detectPreset({
          enabledModes: gameConfig.enabledModes,
          roundLimit: gameConfig.roundLimit,
          roundTimer: gameConfig.roundTimer,
          maxSkips: gameConfig.maxSkips,
          waitForHost: gameConfig.waitForHost,
          enableJoinDuringRound: gameConfig.enableJoinDuringRound,
        }, playerCountRef.current, detectedEntry);
        setSelectedPreset(detected);
      }
    });
    socket.on("playerId", (playerId: string) => {
      setPlayerId(playerId as string);
    });
    socket.on("setPlayerName", (playerName: string) => {
      setPlayerName(playerName);
    });
    socket.on("hostId", (newHostId: string) => {
      setHostId(newHostId as string);
    });
    socket.on("players", (players) => {
      playerCountRef.current = Object.keys(players).length;
      setPlayers(players);
    });
    socket.on("hostTest", () => {});
    socket.on("invalid", () => setLobbyKey(""));
    socket.on("kicked", () => {
      // Kicked by server (same playerId connected from another location)
      sessionStorage.removeItem("playerId");
      sessionStorage.removeItem("lobbyKey");
      history.push("/play");
    });
    socket.on("adminMessage", (msg: string) => {
      setAdminMessage(msg);
    });
    socket.on("readyPlayers", (ids: string[]) => {
      setReadyPlayerIds(ids);
      // Reconcile the local toggle with the server's (connected-filtered) ready set,
      // so a reconnect can't leave the button out of sync
      setIsReady(ids.includes(playerIdRef.current));
    });
    socket.on("friendPool", (data: { enabled: boolean; names: { id: number; name: string }[] }) => {
      setFriendPoolEnabled(data.enabled);
      setFriendPoolNames(data.names);
    });
    socket.on("selectedDecks", async (selectedDeckIds) => {
      if (autoSelectingDecksRef.current || hasPreSelectedDecks.current) return;
      let backendDeckInfos = await Deck.loadDeckInfos(selectedDeckIds);
      if (isMounted && backendDeckInfos) {
        let deckInfos = convertBackendToFrontendDecks(backendDeckInfos);
        setSelectedDecks(deckInfos);
      }
    });
    socket.on("playerEndResults", (playerEndResults: PlayerEndResults) => {
      setPlayerEndResults(playerEndResults);
    });
    socket.on("results", () => {
      setIsReady(false);
      setReadyPlayerIds([]);
    });
    socket.on("reverseRoundResults", () => {
      setIsReady(false);
      setReadyPlayerIds([]);
    });
    socket.on("reverseRoundStart", () => {
      setIsReady(false);
    });
    socket.on("picassoResults", () => {
      setIsReady(false);
      setReadyPlayerIds([]);
    });
    socket.on("picassoStart", () => {
      setIsReady(false);
    });
    socket.on("ftlStreakStart", () => {
      setIsReady(false);
    });
    socket.on("ftlStreakEnd", () => {
      setIsReady(false);
      setReadyPlayerIds([]);
    });
    socket.on("herdStreakStart", () => {
      setIsReady(false);
    });
    socket.on("herdStreakEnd", () => {
      setIsReady(false);
      setReadyPlayerIds([]);
    });
    socket.on("turnInfo", () => {
      setIsReady(false);
    });
    socket.on("resultCards", (cards: ResultCard[]) => {
      setResultCards(cards);
      track("result_cards_received", { count: cards.length });
    });
    socket.on("turnHistory", (history: TurnInfo[]) => {
      setGameTurnHistory(history);
    });
    socket.on("restartLobby", () => {
      setLobbyPhase("L");
      setPlayerEndResults(undefined);
      setResultCards([]);
      setGameTurnHistory([]);
      setIsReady(false);
      setReadyPlayerIds([]);
    });
    socket.on("disconnect", () => {
      setWasDCed(true); // show reconnecting overlay — socket.io will auto-reconnect
    });
    socket.on("connect", () => {
      if (hasJoinedRef.current) {
        // Reconnect after WiFi drop — re-join so server can restore state
        socket.emit("joinLobby", joinParamsRef.current);
      } else {
        hasJoinedRef.current = true;
      }
      setWasDCed(false);
      setReconnectStatus("");
    });
    // Server saw an event from a socket that hasn't joined yet (fresh reconnect race) — rejoin
    socket.on("rejoinRequired", () => rejoin());
    // Surface reconnect progress on the overlay so a failing reconnect is visible
    const onReconnectAttempt = (attempt: number) =>
      setReconnectStatus("Reconnecting (attempt " + attempt + ")…");
    const onReconnectError = () => setReconnectStatus("Still trying to reconnect…");
    const onReconnectFailed = () => setReconnectStatus("Could not reconnect — check your connection");
    socket.io.on("reconnect_attempt", onReconnectAttempt);
    socket.io.on("reconnect_error", onReconnectError);
    socket.io.on("reconnect_failed", onReconnectFailed);
    // Mobile browsers freeze timers in the background, so socket.io's retry timer may
    // not have fired yet when the user returns — kick the connection immediately
    const wake = () => {
      if (document.visibilityState === "visible" && !socket.connected) {
        socket.connect();
      }
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("pageshow", wake);
    socket.connect();
    // Only emit joinLobby if we have a real key — prevents a phantom "" lobby
    // on page refresh before GameContextProvider has restored the session.
    if (lobbyKey) {
      track("lobby_joined", { lobbyKey });
      socket.emit("joinLobby", joinParamsRef.current);
    }

    return () => {
      isMounted = false;
      // sessionStorage is intentionally NOT cleared here — the browser clears it on tab close,
      // and React cleanup also fires on page refresh, which would break the reconnect flow.
      // sessionStorage is only cleared on explicit server events (kicked, invalid).
      setLobbyKey("");
      setSubmittedPlayerName("");
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("pageshow", wake);
      socket.io.off("reconnect_attempt", onReconnectAttempt);
      socket.io.off("reconnect_error", onReconnectError);
      socket.io.off("reconnect_failed", onReconnectFailed);
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    setIsHost(hostId === playerId && hostId != "");
  }, [hostId]);

  useEffect(() => {
    if (!isHost || !socket) return;

    // Check for pre-selected decks from config link
    const preSelectedDeckIds = sessionStorage.getItem("preSelectedDeckIds");
    if (preSelectedDeckIds) {
      try {
        const deckIds = JSON.parse(preSelectedDeckIds);
        if (Array.isArray(deckIds) && deckIds.length > 0) {
          // Emit to server to set these decks
          socket.emit("setSelectedDecks", deckIds);

          // Also fetch full deck info and update local state
          // (server broadcasts to others via castToOthers, host doesn't receive it back)
          (async () => {
            const backendDeckInfos = await Deck.loadDeckInfos(deckIds);
            if (backendDeckInfos) {
              setSelectedDecks(convertBackendToFrontendDecks(backendDeckInfos));
            }
            // Allow future selectedDecks events from server to be processed
            hasPreSelectedDecks.current = false;
          })();

          // Switch to manual deck mode so the selected decks are visible
          setDeckMode("manual");

          // Clear from session storage so it doesn't apply again
          sessionStorage.removeItem("preSelectedDeckIds");
        }
      } catch (e) {
        console.error("Failed to apply pre-selected decks", e);
        sessionStorage.removeItem("preSelectedDeckIds");
      }
    }
  }, [isHost, socket]);

  // Auto-select 3 featured decks in the user's language when the host first joins.
  // Blocks the selectedDecks socket handler during the fetch to prevent race conditions.
  useEffect(() => {
    if (!isHost || hasPreSelectedDecks.current) return;
    autoSelectingDecksRef.current = true;
    (async () => {
      let result: CardDeckPreview[] = (await Deck.getRandomDecks(topicLanguage, true)) ?? [];
      if (result.length < 3) {
        result = (await Deck.getRandomDecks(topicLanguage, false)) ?? [];
      }
      if (result.length > 0) {
        setSelectedDecks(result.slice(0, 3));
      }
      autoSelectingDecksRef.current = false;
    })();
  }, [isHost]);

  useEffect(() => {
    if (isHost) {
      let deckIds = selectedDecks.map((deck) => deck.id);
      socket.emit("setSelectedDecks", deckIds);
    }
    let newDeckPreferences = deckPreferences;
    _.forEach(Object.keys(deckPreferences), (deckId) => {
      if (
        !_.contains(
          selectedDecks.map((selectDeck) => selectDeck.id),
          deckId
        )
      ) {
        delete newDeckPreferences[deckId];
        console.log("deleted Deck preference for " + deckId);
      }
    });
    setDeckPreferences(newDeckPreferences);
  }, [selectedDecks]);

  const handleNumberOfRoundsChange = (event: SelectChangeEvent) => {
    setNumberOfRounds(event.target.value as string);
    markConfigTouched();
  };

  const handleRoundTimerChange = (event: SelectChangeEvent) => {
    setRoundTimer(event.target.value as string);
    markConfigTouched();
  };

  const handleMaxSkipsChange = (event: SelectChangeEvent) => {
    setMaxSkips(event.target.value as string);
    markConfigTouched();
  };

  const [allAboutMe, setAllAboutMe] = useState(defaultAllAboutMe);
  const handleAllAboutMeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAllAboutMe(event.target.checked);
    markConfigTouched();
  };

  const [waitForHost, setWaitForHost] = useState(defaultWaitForHost);
  const handleWaitForHostChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWaitForHost(event.target.checked);
    markConfigTouched();
  };

  const [enableJoinDuringRound, setEnableJoinDuringRound] = useState(defaultEnableJoinDuringRound);
  const handleEnableJoinDuringRoundChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnableJoinDuringRound(event.target.checked);
    markConfigTouched();
  };

  useEffect(() => {
    // Don't broadcast (and thus don't echo back) any config until the host has picked a vibe —
    // this keeps the Versus/Co-op choice as a genuine first step before the presets appear.
    if (isHost && lobbyPhase === "L" && entryChosen) {
      socket.emit("setGameConfig", {
        roundLimit: parseInt(numberOfRounds),
        roundTimer: parseInt(roundTimer),
        maxSkips: parseInt(maxSkips),
        allAboutMe: allAboutMe,
        enableJoinDuringRound: enableJoinDuringRound,
        waitForHost: waitForHost,
        enabledModes: enabledModes,
        preset: selectedPreset,
        entryMode,
        deckMode,
        selectedTopics,
        nsfwEnabled,
        includeLowQuality,
        topicLanguage,
      });
    }
  }, [numberOfRounds, roundTimer, maxSkips, allAboutMe, enableJoinDuringRound, waitForHost, enabledModes, selectedPreset, entryMode, entryChosen, deckMode, selectedTopics, nsfwEnabled, includeLowQuality, topicLanguage]);

  // Emit friend pool changes to server
  useEffect(() => {
    if (isHost && lobbyPhase === "L") {
      socket.emit("setFriendPool", { enabled: friendPoolEnabled, names: friendPoolNames });
    }
  }, [friendPoolEnabled, friendPoolNames]);

  // Auto-load friend group from server on mount (if authenticated host)
  useEffect(() => {
    if (isHost && isAuthenticated && !friendPoolLoadedRef.current) {
      friendPoolLoadedRef.current = true;
      (async () => {
        try {
          const token = await getAccessTokenSilently();
          const res = await fetch(serverUrl("/api/user/friend-group"), {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (data.names?.length > 0) {
              setFriendPoolNames(data.names);
            }
          }
        } catch (e) {
          console.warn("Failed to load friend group", e);
        }
      })();
    }
  }, [isHost, isAuthenticated]);

  // Auto-save friend group when it changes (if authenticated host)
  useEffect(() => {
    if (!isHost || !isAuthenticated || !friendPoolLoadedRef.current) return;
    const timer = setTimeout(async () => {
      try {
        const token = await getAccessTokenSilently();
        await fetch(serverUrl("/api/user/friend-group"), {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ names: friendPoolNames.map((n) => n.name) }),
        });
      } catch (e) {
        console.warn("Failed to save friend group", e);
      }
    }, 1000); // debounce
    return () => clearTimeout(timer);
  }, [friendPoolNames, isHost, isAuthenticated]);

  const handleDeckPreferenceChange = (
    newValue: number,
    deckId: string
  ) => {
    console.log("preference registered " + newValue);
    const updated = { ...deckPreferences, [deckId]: newValue };
    setDeckPreferences(updated);
    socket.emit("setDeckPreference", { deckId: deckId, newValue });
  };

  // Browser back button closes deck browser instead of navigating away
  useEffect(() => {
    if (selectDecks) {
      window.history.pushState({ deckBrowser: true }, "");
      const onPopState = () => setSelectDecks(false);
      window.addEventListener("popstate", onPopState);
      return () => window.removeEventListener("popstate", onPopState);
    }
  }, [selectDecks]);

  const handleCreateNewDeck = async () => {
    if (isAuthenticated) {
      let token = "";
      console.log("starting token request");
      token = await getAccessTokenSilently();
      console.log("ending token request");
      let deckId = await Deck.createDeck(token, "en");
      if (deckId) {
        console.log("received deck id " + JSON.stringify(deckId));
        setLastEditedDeck(deckId);
        setCreateDecks(true);
      }
    } else {
      loginWithPopup();
    }
  };

  const removeDeck = (deck: CardDeckPreview) => {
    setSelectedDecks(selectedDecks.filter((d) => d.id !== deck.id));
  };

  const handleLobbyDeselectClick = (deck: CardDeckPreview) => {
    if (skipDeselectionConfirm) {
      removeDeck(deck);
    } else {
      setDeselectionTarget(deck);
    }
  };

  if (lobbyPhase === "L") {
    // Full-screen deck creator overlay
    if (createDecks) {
      return (
        <Box sx={{ position: "fixed", inset: 0, zIndex: 10, bgcolor: "background.default", overflow: "auto" }}>
          <CardDeckCreator deckId={lastEditedDeck} goBack={() => setCreateDecks(false)} />
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ display: { xs: "none", md: "block" }, position: "fixed", bottom: 16, right: 16, zIndex: 100 }}>
          <LanguageSelector />
        </Box>
        <Box
          sx={{
            minHeight: "100dvh",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Stack
            direction="column"
            alignItems="center"
            spacing={2}
            sx={{ width: "100%", maxWidth: 860, px: 2, py: 2, pb: 4, flex: 1 }}
          >
            {/* Spinning Logo */}
            <FrontPageLogo sx={{ height: "6.5vh", width: "40%", minHeight: 42, marginTop: 0 }} />

            {/* Two-panel layout. CSS grid avoids MUI Grid's negative spacing margins on phones. */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md: "minmax(0, 1fr) minmax(0, 2fr)",
                },
                gap: 2,
                width: "100%",
                minWidth: 0,
              }}
            >
              {/* Left: Players panel */}
              <Box sx={{ minWidth: 0 }}>
                <Paper elevation={0} sx={{ width: "100%", display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "#FAF9F7", borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)" }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, pt: 1, pb: 1 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: "bold" }}>
                      {t("lobby_players")} &middot; {playerCount}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {!isMobile && (
                        <IconButton size="small" onClick={() => setQrOpen(true)} sx={{ p: 0.25 }}>
                          <QrCode2Icon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          fontSize: "1.1rem",
                          letterSpacing: "0.18em",
                          filter: isCodeVisible ? "none" : "blur(6px)",
                          transition: "filter 0.2s",
                          userSelect: isCodeVisible ? "text" : "none",
                        }}
                      >
                        {lobbyKey}
                      </Typography>
                      <IconButton size="small" onClick={() => setIsCodeVisible(!isCodeVisible)} sx={{ p: 0.25 }}>
                        {isCodeVisible ? <VisibilityIcon sx={{ fontSize: 14 }} /> : <VisibilityOffIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </Stack>
                  </Stack>
                  {isMobile ? (
                    <Box sx={{ position: "relative" }}>
                      <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", px: 1.5, py: 1, pt: "10px", pr: 5 }}>
                        {Object.keys(players).map((pid, i) => {
                          const color = getPlayerColor(i);
                          const isHostPlayer = pid === hostId;
                          const isReadyPlayer = readyPlayerIds.includes(pid);
                          return (
                            <Stack
                              key={pid}
                              alignItems="center"
                              spacing={0.5}
                              sx={{ minWidth: 52, cursor: isHost && pid !== playerId ? "pointer" : "default" }}
                              onClick={() => { if (isHost && pid !== playerId) setKickTarget(pid); }}
                            >
                              <Box sx={{ position: "relative" }}>
                                <PlayerAvatar color={color} size={40} />
                                {isHostPlayer && (
                                  <Box sx={{ position: "absolute", bottom: -2, right: -2, bgcolor: "background.paper", borderRadius: "50%", lineHeight: 0, p: "2px" }}>
                                    <FaCrown style={{ fontSize: 9, color: "#D36667" }} />
                                  </Box>
                                )}
                                {!waitForHost && (
                                  <Box sx={{ position: "absolute", top: -3, right: -3 }}>
                                    {isReadyPlayer
                                      ? <CheckCircleIcon sx={{ fontSize: 14, color: "success.main", bgcolor: "background.paper", borderRadius: "50%" }} />
                                      : <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: "text.disabled", bgcolor: "background.paper", borderRadius: "50%" }} />}
                                  </Box>
                                )}
                              </Box>
                              <Typography variant="caption" textAlign="center" noWrap sx={{ maxWidth: 56, fontSize: "0.6rem" }}>
                                {players[pid].name}
                              </Typography>
                            </Stack>
                          );
                        })}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => setQrOpen(true)}
                        sx={{ position: "absolute", bottom: 8, right: 8, bgcolor: "background.paper", boxShadow: "0 1px 4px rgba(0,0,0,0.12)", p: 0.5 }}
                      >
                        <QrCode2Icon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ minHeight: 120, overflowY: "auto" }}>
                      {(() => {
                        const ROW_HEIGHT = 48;
                        const pids = Object.keys(players);
                        return (
                          <>
                            {pids.map((pid, i) => {
                              const color = getPlayerColor(i);
                              const isHostPlayer = pid === hostId;
                              const isReadyPlayer = readyPlayerIds.includes(pid);
                              return (
                                <Box
                                  key={pid}
                                  sx={{
                                    display: "flex", alignItems: "center", gap: 1.5, px: 1.5, height: ROW_HEIGHT,
                                    cursor: isHost && pid !== playerId ? "pointer" : "default",
                                    "&:hover": isHost && pid !== playerId ? { bgcolor: "action.hover" } : {},
                                    borderRadius: 1,
                                  }}
                                  onClick={() => { if (isHost && pid !== playerId) setKickTarget(pid); }}
                                >
                                  <Box sx={{ position: "relative" }}>
                                    <PlayerAvatar color={color} size={32} />
                                    {isHostPlayer && (
                                      <Box sx={{ position: "absolute", bottom: -2, right: -2, bgcolor: "background.paper", borderRadius: "50%", lineHeight: 0, p: "2px" }}>
                                        <FaCrown style={{ fontSize: 9, color: "#D36667" }} />
                                      </Box>
                                    )}
                                  </Box>
                                  <Typography variant="body2" sx={{ flex: 1, fontWeight: isHostPlayer ? 600 : 400 }} noWrap>
                                    {players[pid].name}
                                  </Typography>
                                  {isHost && pid !== playerId && (
                                    <CloseIcon sx={{ fontSize: 16, color: "text.disabled", "&:hover": { color: "error.main" } }} />
                                  )}
                                  {!waitForHost && (
                                    isReadyPlayer
                                      ? <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
                                      : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                                  )}
                                </Box>
                              );
                            })}
                            {pids.length < 2 && (
                              <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 2, display: "block", textAlign: "center" }}>
                                Waiting for players...
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  )}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
                    <Button size="small" variant="outlined"
                      startIcon={linkCopied ? <CheckCircleIcon sx={{ color: "success.main" }} /> : <ContentCopyIcon />}
                      onClick={copyInviteLink}
                      color={linkCopied ? "success" : "primary"}>
                      {linkCopied ? t("lobby_copied") : t("lobby_invite")}
                    </Button>
                    {!isMobile && (
                      <Button color="error" size="small" sx={{ textTransform: "none" }}
                        onClick={() => setLeaveDialogOpen(true)}>{t("leave")}</Button>
                    )}
                  </Stack>
                </Paper>
              </Box>

              {/* Right: 3-tab panel */}
              <Box sx={{ minWidth: 0 }}>
                <Paper elevation={0} sx={{ width: "100%", overflow: "hidden", display: "flex", flexDirection: "column", bgcolor: "#FAF9F7", borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)" }}>
                  <Tabs
                    value={rightTab}
                    onChange={(_, v) => openTab(v)}
                    variant="fullWidth"
                    sx={{
                      minHeight: 36,
                      px: 0.5,
                      pt: 0.5,
                      "& .MuiTab-root": { minHeight: 36, borderRadius: 1.5, py: 0.5, fontSize: "0.8rem", textTransform: "none" },
                      "& .MuiTabs-indicator": { display: "none" },
                      "& .Mui-selected": { bgcolor: "rgba(211,102,103,0.10)", fontWeight: 700, color: "primary.main" },
                    }}
                  >
                    <Tab label={t("lobby_tab_presets")} value="presets" />
                    <Tab label={t("lobby_tab_questions")} value="decks" />
                    <Tab label={t("lobby_tab_custom")} value="custom" />
                  </Tabs>

                  <Box sx={{ minHeight: { md: 320 }, overflowY: { md: "auto" }, overflowX: "hidden", minWidth: 0 }}>
                  {/* Presets panel */}
                  {rightTab === "presets" && (
                    <Box sx={{ p: 1 }}>
                      {!entryChosen ? (
                        // Step 1 — pick the family. Presets only appear after this.
                        isHost ? (
                          <>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.75, display: "block" }}>
                              {t("entry_choose")}
                            </Typography>
                            <GameModeEntrySelector selected={null} onSelect={handleEntrySelect} />
                          </>
                        ) : (
                          // Non-hosts get plain text rather than a grid of tiles they can't press.
                          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                            {t("host_is_configuring")}
                          </Typography>
                        )
                      ) : (
                        // Step 2 — the chosen family (with a way back) + its presets.
                        <>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.75 }}>
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                              <EntryModeBadge mode={entryMode} />
                              <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
                                {!isHost ? t("entry_host_picked")
                                  : selectedPreset === "CUSTOM" ? t("lobby_tab_custom")
                                  : t("entry_pick_preset")}
                              </Typography>
                            </Stack>
                            {isHost && (
                              <Button size="small" variant="text" sx={{ textTransform: "none", minWidth: 0, p: 0.25 }}
                                onClick={() => setEntryChosen(false)}>
                                {t("entry_change")}
                              </Button>
                            )}
                          </Stack>
                          <LobbyPresetSelector
                            entryMode={entryMode}
                            selected={selectedPreset}
                            onSelect={handlePresetSelect}
                            onOpenCustom={handleOpenCustom}
                            disabled={!isHost}
                          />
                        </>
                      )}
                    </Box>
                  )}

                  {/* Decks panel */}
                  {rightTab === "decks" && (
                    <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", minHeight: { md: 320 } }}>
                      {/* Segmented deck mode toggle */}
                      <Box sx={{
                        display: "flex",
                        bgcolor: "rgba(0,0,0,0.06)",
                        borderRadius: 2,
                        p: 0.4,
                        mb: 1.5,
                        gap: 0.4,
                      }}>
                        {(["topics", "manual"] as const).map((mode) => {
                          const active = deckMode === mode;
                          return (
                            <Box
                              key={mode}
                              onClick={() => isHost && setDeckMode(mode)}
                              sx={{
                                flex: 1,
                                py: 0.6,
                                textAlign: "center",
                                borderRadius: 1.5,
                                cursor: isHost ? "pointer" : "default",
                                bgcolor: active ? "background.paper" : "transparent",
                                boxShadow: active ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                                transition: "all 0.15s ease",
                                "&:hover": isHost && !active ? { bgcolor: "rgba(255,255,255,0.5)" } : {},
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={active ? 600 : 400}
                                color={active ? "text.primary" : "text.secondary"}
                                sx={{ fontSize: "0.82rem", userSelect: "none" }}
                              >
                                {mode === "topics" ? t("lobby_by_topics") : t("lobby_pick_decks")}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>

                      {deckMode === "topics" ? (
                        <Box sx={{ flex: 1 }}>
                          <TopicSelector
                            selectedTopics={selectedTopics}
                            onTopicsChange={setSelectedTopics}
                            nsfwEnabled={nsfwEnabled}
                            onNsfwChange={setNsfwEnabled}
                            includeLowQuality={includeLowQuality}
                            onIncludeLowQualityChange={setIncludeLowQuality}
                            language={topicLanguage}
                            onLanguageChange={setTopicLanguage}
                            friendPoolEnabled={friendPoolEnabled}
                            onFriendPoolEnabledChange={setFriendPoolEnabled}
                            disabled={!isHost}
                          />
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                            {selectedDecks.length > 0 ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, pb: 0.5 }}>
                                {selectedDecks.map((deck) => (
                                  <Box key={deck.id} sx={{ width: "6em" }}>
                                    <CardDeck
                                      compact
                                      featured={deck.featured}
                                      title={deck.name}
                                      id={deck.id}
                                      selected={true}
                                      onPreview={() => setPreviewDeckId(deck.id)}
                                      onSelectToggle={isHost ? () => handleLobbyDeselectClick(deck) : undefined}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Box
                                onClick={isHost ? () => { setDeckBrowserBrowseOnly(false); setSelectDecks(true); } : undefined}
                                sx={{
                                  py: 3, border: "2px dashed", borderColor: "divider",
                                  borderRadius: 2, textAlign: "center",
                                  cursor: isHost ? "pointer" : "default",
                                  "&:hover": isHost ? { borderColor: "primary.main" } : {},
                                }}
                              >
                                <Typography variant="body2" color="text.secondary">
                                  {isHost ? "Select a deck to play" : "No deck selected"}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
                            {isHost && selectedDecks.length > 0 && (
                              <Button size="small" onClick={(e) => setCreateConfigMenuAnchor(e.currentTarget)}>Create link</Button>
                            )}
                            <Box sx={{ flex: 1 }} />
                            {isHost
                              ? <Button size="small" variant="text" onClick={() => { setDeckBrowserBrowseOnly(false); setSelectDecks(true); }}>
                                  {selectedDecks.length === 0 ? "Browse decks" : "+ Add deck"}
                                </Button>
                              : <Button size="small" variant="text" onClick={() => { setDeckBrowserBrowseOnly(true); setSelectDecks(true); }}>
                                  Browse decks
                                </Button>
                            }
                          </Stack>
                        </>
                      )}
                    </Box>
                  )}

                  {/* Custom panel */}
                  {rightTab === "custom" && (
                    <Box sx={{ px: 1.5, py: 1.5 }}>
                      <Stack spacing={1.5}>
                        {/* Game Modes — split into the two families */}
                        <Box sx={{ bgcolor: "rgba(0,0,0,0.02)", borderRadius: 2, p: 1.5 }}>
                          <Stack spacing={1.25}>
                            {MODE_GROUPS.map((group) => (
                              <Box key={group.family}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: "block" }}>
                                  {t(group.headerKey)}
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {group.modes.map((mode) => {
                                    const active = enabledModes.includes(mode.value);
                                    return (
                                      <Chip
                                        key={mode.value}
                                        label={t(mode.labelKey)}
                                        size="small"
                                        sx={{
                                          borderRadius: 99,
                                          fontSize: "0.75rem",
                                          fontWeight: active ? 600 : 400,
                                          transition: "all 0.15s",
                                        }}
                                        onClick={isHost ? () => handleModeToggle(mode.value) : undefined}
                                        color={active ? "primary" : "default"}
                                        variant={active ? "filled" : "outlined"}
                                      />
                                    );
                                  })}
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                          {needsFamiliarity && (
                            <Alert severity="info" icon={false} sx={{ mt: 1.25, py: 0.25, fontSize: "0.75rem", "& .MuiAlert-message": { py: 0.5 } }}>
                              {t("lobby_mixed_modes_warning")}
                            </Alert>
                          )}
                        </Box>

                        {/* Settings */}
                        <Box sx={{ bgcolor: "rgba(0,0,0,0.02)", borderRadius: 2, p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.75, display: "block" }}>Settings</Typography>
                          <Stack spacing={1}>
                            {[
                              { label: "Rounds", choices: numberOfRoundsChoices, value: numberOfRounds, set: setNumberOfRounds },
                              { label: "Timer", choices: roundTimerChoices, value: roundTimer, set: setRoundTimer, suffix: "s" },
                              { label: "Skips", choices: maxSkipsChoices, value: maxSkips, set: setMaxSkips },
                            ].map((row) => (
                              <Stack key={row.label} direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary">{row.label}</Typography>
                                <ToggleButtonGroup
                                  size="small"
                                  exclusive
                                  value={row.value}
                                  onChange={(_, v) => { if (v !== null && isHost) { row.set(v); setSelectedPreset("CUSTOM"); } }}
                                  sx={{
                                    flexWrap: "wrap",
                                    "& .MuiToggleButton-root": {
                                      px: 1, py: 0.25, fontSize: "0.72rem", borderRadius: "99px !important",
                                      border: "1px solid rgba(0,0,0,0.08)",
                                      textTransform: "none",
                                      minWidth: 0,
                                      "&.Mui-selected": { bgcolor: "#D3666715", color: "#D36667", borderColor: "#D3666740", fontWeight: 700 },
                                    },
                                    gap: 0.4,
                                    "& .MuiToggleButtonGroup-grouped": { border: "1px solid rgba(0,0,0,0.08) !important", ml: "0 !important" },
                                  }}
                                >
                                  {row.choices.map((c) => (
                                    <ToggleButton key={c} value={c} disabled={!isHost}>
                                      {c}{row.suffix || ""}
                                    </ToggleButton>
                                  ))}
                                </ToggleButtonGroup>
                              </Stack>
                            ))}
                            {/* Spells out what "rounds" actually costs, without explaining czars. */}
                            <Typography
                              variant="caption"
                              textAlign="right"
                              sx={{ color: matchIsLong ? "warning.main" : "text.secondary", fontWeight: matchIsLong ? 600 : 400 }}
                            >
                              {/* In versus a round IS a question, so spelling both out would be noise. */}
                              {t(estimatedQuestions === parseInt(numberOfRounds)
                                ? "lobby_rounds_estimate_flat"
                                : "lobby_rounds_estimate", {
                                rounds: parseInt(numberOfRounds),
                                questions: estimatedQuestions,
                                minutes: estimatedMinutes,
                              })}
                            </Typography>
                          </Stack>
                        </Box>

                        {/* Options */}
                        <Box sx={{ bgcolor: "rgba(0,0,0,0.02)", borderRadius: 2, p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: "block" }}>Options</Typography>
                          <Stack spacing={0}>
                            <SettingSwitch toolTip="Players can join after start" setting="Join mid-round" checked={enableJoinDuringRound} check={handleEnableJoinDuringRoundChange} readonly={!isHost} />
                            <SettingSwitch toolTip="Host controls progression" setting="Host controls" checked={waitForHost} check={handleWaitForHostChange} readonly={!isHost} />
                          </Stack>
                        </Box>

                        {/* Friend Pool */}
                        <FriendPoolEditor
                          enabled={friendPoolEnabled}
                          onEnabledChange={setFriendPoolEnabled}
                          names={friendPoolNames}
                          onNamesChange={setFriendPoolNames}
                          playerNames={Object.values(players).map((p) => p.name)}
                          isHost={isHost}
                          isAuthenticated={isAuthenticated}
                          onLoginClick={() => loginWithPopup()}
                        />
                      </Stack>
                    </Box>
                  )}
                  </Box>
                </Paper>
                {/* Ready / Start — right-aligned on desktop */}
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
                  {waitForHost ? (
                    isHost ? (
                      <Tooltip title={startBlockedReason} arrow>
                        <span>
                          <Button disabled={!canStart} variant="contained"
                            sx={{ px: 4, py: 1, fontWeight: 700, borderRadius: 99, fontSize: "0.95rem", letterSpacing: "0.03em", boxShadow: "0 2px 8px rgba(211,102,103,0.3)" }}
                            onClick={() => guardLongMatch(() => socket.emit("startGame"))}>
                            Start Game
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Waiting for host...</Typography>
                    )
                  ) : (
                    <Tooltip title={startBlockedReason} arrow>
                      <span>
                        <Button disabled={!canStart} variant={isReady ? "outlined" : "contained"}
                          sx={{
                            px: 4, py: 1, fontWeight: 700, borderRadius: 99, fontSize: "0.95rem", letterSpacing: "0.03em",
                            boxShadow: isReady ? "none" : "0 2px 8px rgba(211,102,103,0.3)",
                          }}
                          onClick={() => {
                            const next = !isReady;
                            const apply = () => {
                              setIsReady(next);
                              socket.emit(next ? "setReady" : "unsetReady");
                            };
                            // Only gate readying up, never un-readying.
                            if (next) guardLongMatch(apply); else apply();
                          }}>
                          {isReady ? t("unready") : t("ready")} ({readyPlayerIds.length}/{Object.keys(players).length})
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            </Box>

            <CreateDeckConfigMenu
              selectedDeckIds={selectedDecks.map((d) => d.id)}
              anchorEl={createConfigMenuAnchor}
              open={Boolean(createConfigMenuAnchor)}
              onClose={() => setCreateConfigMenuAnchor(null)}
              lobbyName={submittedPlayerName}
              authUserName={user?.name}
            />

            <Box sx={{ flex: 1 }} />
            {/* Leave (mobile only) */}
            {isMobile && (
              <Button color="error" size="small" sx={{ textTransform: "none" }}
                onClick={() => setLeaveDialogOpen(true)}>Leave lobby</Button>
            )}
          </Stack>
        </Box>

        {/* QR dialog */}
        <Dialog open={qrOpen} onClose={() => setQrOpen(false)}>
          <DialogTitle>Invite link</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <QRCodeCanvas value={inviteLink} size={200} />
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all", maxWidth: 220 }}>
              {inviteLink}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setQrOpen(false)}>{t("close")}</Button>
            <Button
              startIcon={linkCopied ? <CheckCircleIcon sx={{ color: "success.main" }} /> : undefined}
              color={linkCopied ? "success" : "primary"}
              onClick={copyInviteLink}>
              {linkCopied ? "Copied!" : "Copy link"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Long-match confirmation — a big co-op lobby multiplies rounds by player count,
            so the host is told the real length before committing everyone to it. */}
        <Dialog open={longMatchOpen} onClose={() => setLongMatchOpen(false)}>
          <DialogTitle>{t("lobby_long_match_title")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("lobby_long_match_body", {
                questions: estimatedQuestions,
                minutes: estimatedMinutes,
                players: playerCount,
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { pendingStartRef.current = null; setLongMatchOpen(false); }}>
              {t("cancel", "Cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                ackedMinutesRef.current = estimatedMinutes;
                setLongMatchOpen(false);
                pendingStartRef.current?.();
                pendingStartRef.current = null;
              }}
            >
              {t("lobby_understand")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Deselect deck confirmation dialog */}
        <Dialog open={deselectionTarget !== null} onClose={() => setDeselectionTarget(null)}>
          <DialogTitle>Remove deck?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Remove <strong>{deselectionTarget?.name}</strong> from the lobby? You'll need to search for it again to re-add it.
            </DialogContentText>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={skipDeselectionConfirm}
                  onChange={(e) => {
                    setSkipDeselectionConfirm(e.target.checked);
                    localStorage.setItem("skipDeselectionConfirm", String(e.target.checked));
                  }}
                />
              }
              label="Don't ask again"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeselectionTarget(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                if (deselectionTarget) removeDeck(deselectionTarget);
                setDeselectionTarget(null);
              }}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        {/* Leave confirmation dialog */}
        <Dialog open={leaveDialogOpen} onClose={() => setLeaveDialogOpen(false)}>
          <DialogTitle>Leave lobby?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to leave? You'll be removed from the lobby.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLeaveDialogOpen(false)}>Stay</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                socket.emit("leaveLobby");
                sessionStorage.removeItem("playerId");
                sessionStorage.removeItem("lobbyKey");
                history.push("/play");
              }}
            >
              Leave
            </Button>
          </DialogActions>
        </Dialog>

        {/* Kick confirmation dialog */}
        <Dialog open={kickTarget !== null} onClose={() => setKickTarget(null)}>
          <DialogTitle>Kick player?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Kick <strong>{kickTarget ? players[kickTarget]?.name : ""}</strong> from the lobby?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setKickTarget(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                if (kickTarget) {
                  socket.emit("kickPlayer", { targetPlayerId: kickTarget });
                  setKickTarget(null);
                }
              }}
            >
              Kick
            </Button>
          </DialogActions>
        </Dialog>

        {/* Disconnection overlay */}
        <Backdrop open={wasDCed} sx={{ zIndex: 9999, bgcolor: "rgba(0,0,0,0.6)", flexDirection: "column", gap: 2 }}>
          <WifiOffIcon sx={{ fontSize: 80, color: "white" }} />
          <Typography variant="h5" color="white" textAlign="center">
            Connection lost - trying to get you back in…
          </Typography>
          {reconnectStatus && (
            <Typography variant="body2" color="white" textAlign="center" sx={{ opacity: 0.8 }}>
              {reconnectStatus}
            </Typography>
          )}
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => rejoin(false)}>
            Tap to reconnect
          </Button>
        </Backdrop>

        {/* Deck browser dialog */}
        <Dialog
          open={selectDecks}
          onClose={() => setSelectDecks(false)}
          fullScreen={isMobile}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              ...(!isMobile && { height: "90vh", maxHeight: 800 }),
              bgcolor: "background.default",
              overflow: "hidden",
            },
          }}
          BackdropProps={{ sx: { bgcolor: "rgba(0,0,0,0.3)" } }}
        >
          <CardDeckBrowser
            goCreateDecks={() => handleCreateNewDeck()}
            goBack={() => setSelectDecks(false)}
            selectedDecks={selectedDecks}
            setSelectedDecks={(newSelectedDecks: CardDeckPreview[]) =>
              setSelectedDecks(newSelectedDecks)
            }
            setPreviewDeckId={(previewDeckId: string) => setPreviewDeckId(previewDeckId)}
            browseOnly={deckBrowserBrowseOnly}
          />
        </Dialog>

        <Backdrop open={previewDeckId !== ""} sx={{ overflow: "scroll", zIndex: 1301 }}>
          <CardDeckViewer
            previewDeckId={previewDeckId}
            setClose={() => setPreviewDeckId("")}
            friendPool={[
              ...Object.entries(players)
                .filter(([pid]) => pid !== playerId)
                .map(([, p]) => p.name),
              ...(friendPoolEnabled
                ? friendPoolNames
                    .map((n) => n.name)
                    .filter((name) => !Object.values(players).some((p) => p.name === name))
                : []),
            ]}
          />
        </Backdrop>
        <AdminMessageDialog
          message={adminMessage}
          onDismiss={() => setAdminMessage(null)}
          onReply={(text) => socket.emit("adminMessageReply", { text })}
        />
      </>
    );
  } else if (lobbyPhase === "R") {
    return (
      <>
        <Game
          baseUrl={baseUrl}
          players={players}
          playerId={playerId}
          isHost={isHost}
          hostId={hostId}
          playerName={playerName}
          waitForHost={waitForHost}
          wasDCed={wasDCed}
          reconnectStatus={reconnectStatus}
          rejoin={() => rejoin(false)}
          readyPlayerIds={readyPlayerIds}
          isReady={isReady}
          onReadyToggle={(next: boolean) => {
            setIsReady(next);
            socket.emit(next ? "setReady" : "unsetReady");
          }}
        />
        <AdminMessageDialog
          message={adminMessage}
          onDismiss={() => setAdminMessage(null)}
          onReply={(text) => socket.emit("adminMessageReply", { text })}
        />
      </>
    );
  } else {
    return (
      <>
        <EndPanel
          playerName={players[playerId]?.name as string}
          isHost={isHost}
          playerEndResults={playerEndResults}
          resultCards={resultCards}
          turnHistory={gameTurnHistory}
          players={players}
          waitForHost={waitForHost}
          isReady={isReady}
          readyPlayerIds={readyPlayerIds}
          totalPlayers={Object.keys(players).length}
          onReadyToggle={(ready) => {
            setIsReady(ready);
            socket.emit(ready ? "setReady" : "unsetReady");
          }}
        />
        <AdminMessageDialog
          message={adminMessage}
          onDismiss={() => setAdminMessage(null)}
          onReply={(text) => socket.emit("adminMessageReply", { text })}
        />
      </>
    );
  }
}
